import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Sparkles, FileText, Trash2, Download, Save, Wand2, Loader2, ImageIcon } from "lucide-react";

const I = "w-full bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm text-paper";

function FieldEditor({ ck, value, onChange }) {
  const label = ck.replace(/_/g, " ");
  if (Array.isArray(value)) {
    const objs = value.length > 0 && typeof value[0] === "object";
    const text = objs ? JSON.stringify(value, null, 2) : value.join("\n");
    return (
      <label className="block">
        <span className="text-[11px] uppercase tracking-wide text-paper/40">{label}{objs ? " (JSON)" : " (one per line)"}</span>
        <textarea rows={objs ? 6 : 4} value={text} data-testid={`ds-field-${ck}`}
          onChange={(e) => {
            if (objs) { try { onChange(JSON.parse(e.target.value)); } catch { onChange(e.target.value); } }
            else onChange(e.target.value.split("\n"));
          }} className={`${I} mt-1 font-mono text-xs`} />
      </label>
    );
  }
  const long = typeof value === "string" && value.length > 60;
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-paper/40">{label}</span>
      {long
        ? <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`ds-field-${ck}`} className={`${I} mt-1`} />
        : <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`ds-field-${ck}`} className={`${I} mt-1`} />}
    </label>
  );
}

export default function DocStudio() {
  const [meta, setMeta] = useState({ doc_types: [], palettes: [] });
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ doc_type: "flyer", title: "", prompt: "", palette: "heraldic", with_image: true });
  const [gen, setGen] = useState(false);
  const [active, setActive] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);

  const loadDocs = () => api.get("/admin/doc-studio").then((r) => setDocs(r.data));
  useEffect(() => { api.get("/admin/doc-studio/meta").then((r) => setMeta(r.data)); loadDocs(); }, []);

  const loadPreview = (did) => api.get(`/admin/doc-studio/${did}/html`).then((r) => setPreview(r.data.html));
  const openDoc = async (d) => { setActive(d); loadPreview(d.did); };

  const generate = async () => {
    if (!form.prompt.trim()) return toast.error("Describe what you want first");
    setGen(true);
    try {
      const { data } = await api.post("/admin/doc-studio/generate", form);
      toast.success("Document drafted");
      await loadDocs(); openDoc(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setGen(false);
  };

  const setContentKey = (k, v) => setActive({ ...active, content: { ...active.content, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/doc-studio/${active.did}`, { title: active.title, palette: active.palette, content: active.content });
      toast.success("Saved — preview updated");
      setActive(data); loadPreview(data.did); loadDocs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/doc-studio/${active.did}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(active.title || "document").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not export PDF"); }
  };

  const del = async (did) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/admin/doc-studio/${did}`);
    if (active?.did === did) { setActive(null); setPreview(""); }
    loadDocs();
  };

  const contentKeys = useMemo(() => (active ? Object.keys(active.content || {}) : []), [active]);

  return (
    <AdminLayout>
      <div className="p-8 max-w-[1400px]" data-testid="admin-docstudio-page">
        <div className="flex items-center gap-2"><Wand2 className="h-6 w-6 text-heraldic" /><h1 className="font-serif text-3xl">Doc Studio</h1></div>
        <p className="text-paper/50 text-sm mt-1">Turn a brief into a prestigious, on-brand flyer, brochure or admission letter — with AI imagery and a print-ready PDF.</p>

        <div className="mt-6 grid lg:grid-cols-[340px_1fr] gap-6">
          {/* Composer + library */}
          <div className="space-y-5">
            <div className="border border-white/10 rounded-sm p-4 bg-white/5 space-y-3" data-testid="ds-composer">
              <div className="text-xs uppercase tracking-wide text-heraldic">New document</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block"><span className="text-[11px] text-paper/40">Type</span>
                  <select value={form.doc_type} data-testid="ds-type"
                    onChange={(e) => { const dt = meta.doc_types.find((t) => t.key === e.target.value); setForm({ ...form, doc_type: e.target.value, with_image: dt ? dt.default_image : true }); }}
                    className={`${I} mt-1`}>
                    {meta.doc_types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </label>
                <label className="block"><span className="text-[11px] text-paper/40">Palette</span>
                  <select value={form.palette} onChange={(e) => setForm({ ...form, palette: e.target.value })} data-testid="ds-palette" className={`${I} mt-1`}>
                    {meta.palettes.map((p) => <option key={p.key} value={p.key}>{p.key.replace(/_/g, " ")}</option>)}
                  </select>
                </label>
              </div>
              <label className="block"><span className="text-[11px] text-paper/40">Title (optional)</span>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ds-title" className={`${I} mt-1`} placeholder="Auto-generated if blank" /></label>
              <label className="block"><span className="text-[11px] text-paper/40">Brief</span>
                <textarea rows={5} value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} data-testid="ds-prompt" className={`${I} mt-1`} placeholder="e.g. A one-page brochure for our K–12 Accelerated programme aimed at ambitious families in West Africa, emphasising mastery-based learning and Regional Access Tuition." /></label>
              <label className="flex items-center gap-2 text-sm text-paper/60"><input type="checkbox" checked={form.with_image} onChange={(e) => setForm({ ...form, with_image: e.target.checked })} data-testid="ds-with-image" /><ImageIcon className="h-3.5 w-3.5" /> Generate AI hero image</label>
              <button onClick={generate} disabled={gen} data-testid="ds-generate" className="w-full inline-flex items-center justify-center gap-2 bg-heraldic text-white px-4 py-2.5 rounded-sm text-sm disabled:opacity-60">
                {gen ? <><Loader2 className="h-4 w-4 animate-spin" /> Drafting…</> : <><Sparkles className="h-4 w-4" /> Generate</>}
              </button>
            </div>

            <div className="border border-white/10 rounded-sm p-4 bg-white/5" data-testid="ds-library">
              <div className="text-xs uppercase tracking-wide text-paper/40 mb-3">Library</div>
              <div className="space-y-1.5 max-h-[360px] overflow-auto">
                {docs.map((d) => (
                  <div key={d.did} className={`flex items-center gap-2 px-2.5 py-2 rounded-sm cursor-pointer ${active?.did === d.did ? "bg-heraldic/20 border border-heraldic/40" : "hover:bg-white/5"}`} onClick={() => openDoc(d)} data-testid={`ds-doc-${d.did}`}>
                    <FileText className="h-4 w-4 text-paper/40 shrink-0" />
                    <div className="flex-1 min-w-0"><div className="text-sm truncate">{d.title}</div><div className="text-[10px] text-paper/40 capitalize">{d.doc_type.replace(/_/g, " ")}</div></div>
                    <button onClick={(e) => { e.stopPropagation(); del(d.did); }} className="text-red-400/70 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {docs.length === 0 && <div className="text-paper/40 text-sm py-6 text-center">No documents yet.</div>}
              </div>
            </div>
          </div>

          {/* Editor + preview */}
          {active ? (
            <div className="grid xl:grid-cols-[1fr_1fr] gap-5" data-testid="ds-editor">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input value={active.title} onChange={(e) => setActive({ ...active, title: e.target.value })} data-testid="ds-edit-title" className={`${I} font-serif text-base`} />
                  <button onClick={save} disabled={saving} data-testid="ds-save" className="inline-flex items-center gap-1.5 bg-heraldic text-white px-3 py-2 rounded-sm text-sm shrink-0">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
                  <button onClick={downloadPdf} data-testid="ds-pdf" className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 rounded-sm text-sm shrink-0"><Download className="h-4 w-4" /> PDF</button>
                </div>
                <label className="block"><span className="text-[11px] uppercase tracking-wide text-paper/40">Palette</span>
                  <select value={active.palette} onChange={(e) => setActive({ ...active, palette: e.target.value })} className={`${I} mt-1`}>
                    {meta.palettes.map((p) => <option key={p.key} value={p.key}>{p.key.replace(/_/g, " ")}</option>)}
                  </select></label>
                <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
                  {contentKeys.map((k) => <FieldEditor key={k} ck={k} value={active.content[k]} onChange={(v) => setContentKey(k, v)} />)}
                </div>
              </div>
              <div className="border border-white/10 rounded-sm overflow-hidden bg-white" data-testid="ds-preview">
                <iframe title="preview" srcDoc={preview} className="w-full" style={{ height: "80vh", border: 0, transform: "scale(1)" }} />
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-white/10 rounded-sm flex items-center justify-center text-paper/40 min-h-[420px]" data-testid="ds-empty">
              Generate a document or pick one from the library to edit and export.
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
